import type {CmLogger} from "@cm/log"
import {blenderParameterComponents} from "#material/exchange/parameter-components"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import type {MaterialNodeParameters} from "#material/sockets/parameters"
import type {Vec2} from "#material/types/vector"
import {vec2, vec3} from "#material/types/vector"
import type {NodeParameters} from "@cm/graph-v3"
import {color} from "@cm/graph-v3"
import {mapFieldNames, mapFields} from "@cm/utils"
import {filterFieldNames, findFieldName} from "@cm/utils/misc"
import * as changeCase from "change-case"
import {z} from "zod/v4-mini"
import {isBlenderNodeProperty, removeInternalPrefixes} from "#material/exchange/node-properties"
import {isEditorOnly} from "#material/exchange/editor-only-parameters"
import {parameterTypeSchema} from "#material/validation/material-node-data"

// Define all schemas once outside the function to avoid recreation overhead
const parameterSchema = z.object({name: z.string(), value: z.unknown(), type: parameterTypeSchema})

const colorSchema = z.object({
    name: z.string(),
    type: z.literal("color"),
    value: z.array(z.number()),
})
const color4TupleSchema = z.tuple([z.number(), z.number(), z.number(), z.number()])
const color3TupleSchema = z.tuple([z.number(), z.number(), z.number()])

const vector3Schema = z.object({
    type: z.union([z.literal("vector"), z.literal("any")]),
    value: z.tuple([z.number(), z.number(), z.number()]),
})

const vector2Schema = z.object({
    type: z.union([z.literal("vector"), z.literal("any")]),
    value: z.tuple([z.number(), z.number()]),
})

const eulerSchema = z.object({
    type: z.union([z.literal("euler"), z.literal("any")]),
    value: z.tuple([z.number(), z.number(), z.number()]),
})

const array256Schema = z.object({
    type: z.union([z.literal("vector"), z.literal("any")]),
    value: z.array(z.number()),
})

// Handle float values that may be stored as strings in legacy data (e.g., "0.563", ".9")
const stringToNumber = z.pipe(
    z.string(),
    z.transform((val: string) => Number(val)),
)
const coercibleNumber = z.union([z.number(), stringToNumber])

const floatSchema = z.object({
    type: z.union([z.literal("float"), z.literal("any")]),
    value: coercibleNumber,
})

const integerSchema = z.object({
    type: z.union([z.literal("int"), z.literal("any")]),
    value: coercibleNumber,
})

const booleanSchema = z.object({
    type: z.union([z.literal("boolean"), z.literal("any")]),
    value: z.boolean(),
})

const stringSchema = z.object({
    type: z.union([z.literal("string"), z.literal("any")]),
    value: z.string(),
})

const vectorArraySchema = z.object({
    type: z.union([z.literal("colorArray"), z.literal("any")]),
    value: z.array(z.tuple([z.number(), z.number(), z.number()])),
})

const floatArraySchema = z.object({
    type: z.union([z.literal("floatArray"), z.literal("any")]),
    value: z.array(z.number()),
})

const null3Schema = z.tuple([z.null(), z.null(), z.null()])
const nullableFloat3Schema = z.tuple([z.nullable(z.number()), z.nullable(z.number()), z.nullable(z.number())])
const nullableValueSchema = z.union([z.undefined(), z.null(), null3Schema, nullableFloat3Schema])

// UI parameter schemas
const uiBooleanSchema = z.boolean()
const uiLocationSchema = z.object({x: z.number(), y: z.number()})
const uiStringSchema = z.string()

export const exchangeToPlatformParameter = (exchangeParameter: {name: string; value: unknown; type: string}) => {
    const parameter = parameterSchema.parse(exchangeParameter)

    const parameterAsColor = colorSchema.safeParse(parameter)
    if (parameterAsColor.success) {
        const colorValueAs4Tuple = color4TupleSchema.safeParse(parameterAsColor.data.value)
        if (colorValueAs4Tuple.success) {
            return {
                r: colorValueAs4Tuple.data[0],
                g: colorValueAs4Tuple.data[1],
                b: colorValueAs4Tuple.data[2],
                a: colorValueAs4Tuple.data[3],
            }
        }
        const colorValueAs3Tuple = color3TupleSchema.safeParse(parameterAsColor.data.value)
        if (colorValueAs3Tuple.success) {
            return {r: colorValueAs3Tuple.data[0], g: colorValueAs3Tuple.data[1], b: colorValueAs3Tuple.data[2]}
        }

        throw new Error("Unable to parse color value")
    }

    const parameterAsVector3 = vector3Schema.safeParse(parameter)
    if (parameterAsVector3.success) {
        return {
            x: parameterAsVector3.data.value[0],
            y: parameterAsVector3.data.value[1],
            z: parameterAsVector3.data.value[2],
        }
    }

    const parameterAsVector2 = vector2Schema.safeParse(parameter)
    if (parameterAsVector2.success) {
        return {x: parameterAsVector2.data.value[0], y: parameterAsVector2.data.value[1]}
    }

    const parameterAsEuler = eulerSchema.safeParse(parameter)
    if (parameterAsEuler.success) {
        return {
            x: parameterAsEuler.data.value[0],
            y: parameterAsEuler.data.value[1],
            z: parameterAsEuler.data.value[2],
        }
    }

    const parameterAsArray256 = array256Schema.safeParse(parameter)
    if (parameterAsArray256.success && parameterAsArray256.data.value.length === 256) {
        return parameterAsArray256.data.value
    }

    const parameterAsFloat = floatSchema.safeParse(parameter)
    if (parameterAsFloat.success) {
        return parameterAsFloat.data.value
    }

    const parameterAsInteger = integerSchema.safeParse(parameter)
    if (parameterAsInteger.success) {
        return parameterAsInteger.data.value
    }

    const valueAsBoolean = booleanSchema.safeParse(parameter)
    if (valueAsBoolean.success) {
        return valueAsBoolean.data.value
    }

    const valueAsString = stringSchema.safeParse(parameter)
    if (valueAsString.success) {
        return valueAsString.data.value
    }

    const parameterAsVectorArray = vectorArraySchema.safeParse(parameter)
    if (parameterAsVectorArray.success) {
        return parameterAsVectorArray.data.value.map((vec) => {
            return {r: vec[0], g: vec[1], b: vec[2]}
        })
    }

    const parameterAsFloatArray = floatArraySchema.safeParse(parameter)
    if (parameterAsFloatArray.success) {
        return parameterAsFloatArray.data.value
    }

    if (nullableValueSchema.safeParse(parameter.value).success) return undefined

    throw new Error(`Unexpected parameter data: ${parameter.type} ${parameter.value}`)
}

// Schema for parsing parameter objects in the map phase
const parameterObjectSchema = z.object({name: z.string(), type: z.string(), value: z.unknown()})

// material nodes currently store the parameters as JSON arrays
// this function converts them to the new format
export const exchangeToPlatform = (parameters: unknown[], logger: CmLogger): Pick<MaterialNodeParameters<MaterialNodeSockets>, "settings" | "ui"> => {
    const mappedParameters = Object.fromEntries(
        parameters.map((parameter) => {
            const parsedParameter = parameterObjectSchema.parse(parameter)
            return [parsedParameter.name, exchangeToPlatformParameter(parsedParameter)]
        }),
    )

    const settings = mapFieldNames(
        filterFieldNames(
            mappedParameters,
            (key) => !["internal.color", "internal.mute", "internal.label", "internal.location"].includes(key) && !isEditorOnly(key),
        ),
        changeCase.camelCase,
    )
    const internalDisabled = findFieldName(mappedParameters, (key) => key === "internal.mute")
    const parsedInternalDisabled = uiBooleanSchema.safeParse(internalDisabled)
    const internalLocation = findFieldName(mappedParameters, (key) => key === "internal.location")
    const parsedInternalLocation = uiLocationSchema.safeParse(internalLocation)
    const internalColor = findFieldName(mappedParameters, (key) => key === "internal.color")
    const parsedInternalColor = color.safeParse(internalColor)
    const internalLabel = findFieldName(mappedParameters, (key) => key === "internal.label")
    const parsedInternalLabel = uiStringSchema.safeParse(internalLabel)
    const ui = {
        disabled: parsedInternalDisabled.success ? parsedInternalDisabled.data : undefined,
        internalColor: parsedInternalColor.success ? parsedInternalColor.data : undefined,
        internalLabel: parsedInternalLabel.success ? parsedInternalLabel.data : undefined,
        position: parsedInternalLocation.success ? {x: parsedInternalLocation.data.x, y: -parsedInternalLocation.data.y} : {x: 0, y: -0},
    }

    return {
        settings: removeInternalPrefixes(settings, logger),
        ui,
    }
}

export const toExchangeParameters = (
    parameters: Pick<MaterialNodeParameters<MaterialNodeSockets>, "settings" | "ui">,
    nodeClass: string,
): {[name: string]: unknown} => {
    return buildExchangeParameters(parameters.settings, parameters.ui, nodeClass)
}

// convert the new format back to the old format, suitable for being stored in the database
/**
 * Settings Blender is never offered, named as the platform names them because they never earn a Blender name:
 * the plugin invents the UV map index on upload from the object's own UV layers, and the texture revision travels
 * as the node's own textureRevisionId.
 */
export const settingsNotSentToBlender: {[nodeClass: string]: readonly string[]} = {
    NormalMap: ["uvMapIndex"],
    Tangent: ["uvMapIndex"],
    TextureFromRevision: ["textureRevisionLegacyId"],
    UvMap: ["uvMapIndex"],
}

/** Lookup tables Blender computes from the curve or ramp they belong to and has no property to take one back into. */
const isComputedLookupTable = (key: string) => /Cycles(Mapping|RampAlpha|RampColor)Table$/.test(key)

export const isSettingSentToBlender = (nodeClass: string, key: string) => !settingsNotSentToBlender[nodeClass]?.includes(key) && !isComputedLookupTable(key)

export const buildExchangeParameters = (
    parameters: NodeParameters,
    uiParameters: MaterialNodeParameters<MaterialNodeSockets>["ui"],
    nodeClass: string,
): {[name: string]: unknown} => {
    const convertedSettings = mapFields(parameters, (value, key) => {
        if (value === null || value === undefined) {
            return undefined
        }

        if (new Set(["dataObjectId", "imageResource", "imageResources", "controlPoints", "cyclesMappingTable"]).has(key as string)) {
            return undefined
        }

        if (key === "textureMappingRotation") {
            const valueAsEuler = z.tuple([z.number(), z.number(), z.number()]).safeParse(value)
            if (valueAsEuler.success) {
                return {
                    type: "euler",
                    value: valueAsEuler.data,
                }
            }
            const valueAsVec3Euler = vec3.safeParse(value)
            if (valueAsVec3Euler.success) {
                return {
                    type: "euler",
                    value: [valueAsVec3Euler.data.x, valueAsVec3Euler.data.y, valueAsVec3Euler.data.z],
                }
            }
        }

        const valueAsColor = color.safeParse(value)
        if (valueAsColor.success) {
            if (valueAsColor.data.a === undefined) {
                return {
                    type: "color",
                    value: [valueAsColor.data.r, valueAsColor.data.g, valueAsColor.data.b],
                }
            }
            return {
                type: "color",
                value: [valueAsColor.data.r, valueAsColor.data.g, valueAsColor.data.b, valueAsColor.data.a],
            }
        }

        const valueAsVector3 = vec3.safeParse(value)
        if (valueAsVector3.success) {
            return {
                type: "vector",
                value: [valueAsVector3.data.x, valueAsVector3.data.y, valueAsVector3.data.z],
            }
        }

        const valueAsVector2 = vec2.safeParse(value)
        if (valueAsVector2.success) {
            return {
                type: "vector",
                value: [valueAsVector2.data.x, valueAsVector2.data.y],
            }
        }

        const valueArray = z.array(z.number()).safeParse(value)
        if (valueArray.success && valueArray.data.length === 256) {
            return {
                type: "floatArray",
                value: value,
            }
        }

        const parsedColorArray = z.array(color).safeParse(value)
        if (parsedColorArray.success) {
            return {
                type: "colorArray",
                value: parsedColorArray.data.map((color) => [color.r, color.g, color.b]),
            }
        }

        const parsedString = z.string().safeParse(value)
        if (parsedString.success) {
            return {
                type: "string",
                value: parsedString.data,
            }
        }

        const parsedFloat = z.number().safeParse(value)
        if (parsedFloat.success) {
            if (
                key === "internalUvMapIndex" ||
                key === "uvMapIndex" ||
                key === "smoothPasses" ||
                key === "uvChannel" ||
                key === "dataObjectLegacyId" ||
                key === "textureRevisionLegacyId" ||
                key === "squashFrequency" ||
                key === "offsetFrequency"
            ) {
                return {
                    type: "int",
                    value: parsedFloat.data,
                }
            }

            return {
                type: "float",
                value: parsedFloat.data,
            }
        }

        const parsedBoolean = z.boolean().safeParse(value)
        if (parsedBoolean.success) {
            return {
                type: "boolean",
                value: parsedBoolean.data,
            }
        }

        const parsedControlPoints = z.tuple([z.array(vec2), z.array(vec2), z.array(vec2), z.array(vec2)]).safeParse(value)
        if (parsedControlPoints.success) {
            return undefined
            // return {
            //     type: "vector",
            //     value: value,
            // }
        }

        throw new Error(`Unexpected parameter ${key}: ${JSON.stringify(value)} for node ${nodeClass}`)
    })

    const properlyCasedSettings = mapFieldNames(convertedSettings, (key) => toExchangeParameterName(key, nodeClass))

    const controlPoints = parameters?.["controlPoints"] as [Vec2[], Vec2[], Vec2[], Vec2[]] | undefined
    if (controlPoints) {
        controlPoints.forEach((controlPointList, curveIndex) => {
            controlPointList.forEach((controlPoint, i) => {
                properlyCasedSettings[`internal.mapping.curves[${curveIndex}].points[${i}].location`] = {
                    type: "vector",
                    value: [controlPoint.x, controlPoint.y],
                }
                properlyCasedSettings[`internal.mapping.curves[${curveIndex}].points[${i}].handle_type`] = {
                    type: "string",
                    value: "AUTO",
                }
            })
        })
    }

    const internalLocation = uiParameters
        ? {
              type: "vector",
              value: [uiParameters.position?.x, uiParameters.position?.y === undefined ? undefined : -uiParameters.position?.y],
          }
        : undefined
    const internalColor = uiParameters?.internalColor
        ? {type: "color", value: [uiParameters.internalColor.r, uiParameters.internalColor.g, uiParameters.internalColor.b]}
        : undefined
    return {
        ...properlyCasedSettings,
        ...(uiParameters?.disabled === undefined ? {} : {"internal.mute": {type: "boolean", value: uiParameters.disabled}}),
        "internal.color": internalColor,
        "internal.label": uiParameters?.internalLabel === undefined ? undefined : {type: "string", value: uiParameters.internalLabel},
        "internal.location": internalLocation,
    }
}

export const toExchangeParameterList = (
    parameters: NodeParameters,
    uiParameters: MaterialNodeParameters<MaterialNodeSockets>["ui"],
    nodeClass: string,
): {
    name: string
}[] => {
    return Object.entries(buildExchangeParameters(parameters, uiParameters, nodeClass) ?? {})
        .filter(([_, value]) => value !== undefined)
        .map(([name, value]) => ({name, ...(value as object)}))
        .sort((a, b) => a.name.localeCompare(b.name))
}

const acronyms = ["ior", "uv", "bsdf", "ao", "bssrdf"]

/** Blender keeps these in caps wherever they appear, which capitalCase would render as an ordinary word. */
const withAcronymsInCaps = (name: string) =>
    name
        .split(" ")
        .map((word) => (acronyms.includes(word.toLowerCase()) ? word.toUpperCase() : word))
        .join(" ")

/**
 * A settings key names either a node attribute or a socket's default value, and Blender spells the two
 * differently. Only settings are ambiguous: a Voronoi node has both a `distance` attribute and a `Distance`
 * socket.
 */
export const toExchangeParameterName = (key: string, nodeClass: string, kind: "setting" | "socket" = "setting") => {
    // Blender spells a disabled node "muted".
    if (key === "disabled") return "internal.mute"

    const localNonInternalParams: {[str: string]: string[]} = {
        TexMusgrave: ["offset"],
        TexNoise: ["offset"],
        AmbientOcclusion: ["distance"],
        Bump: ["distance"],
    }
    const forceNotInternal = kind === "socket" || localNonInternalParams[nodeClass]?.includes(key) === true

    if (!forceNotInternal && isBlenderNodeProperty(key)) {
        const keyInDotCase = changeCase.dotCase(key)
        const componentReplacements = blenderParameterComponents.map((component) => ({
            snake: component,
            dot: changeCase.dotCase(component),
        }))
        const propertyKey = componentReplacements.reduce((value, component) => value.replaceAll(component.dot, component.snake), keyInDotCase)

        const restoreArrays = (value: string) => {
            //convert .0. to [0]
            //convert .0 at the end to [0]
            return value.replaceAll(/\.([0-9]+)\./g, "[$1].").replace(/\.([0-9]+)$/, "[$1]")
        }
        return `internal.${restoreArrays(propertyKey)}`
    }

    if (acronyms.includes(key)) {
        return key.toUpperCase()
    }

    const snakeCases = ["shadowCatcher"]
    if (snakeCases.includes(key)) {
        return changeCase.snakeCase(key)
    }

    if (key === "heightDx") return "Height_dx"
    if (key === "heightDy") return "Height_dy"

    if (key.endsWith("Float3")) return changeCase.pascalSnakeCase(key).replace("Float3", "FLOAT3")

    //If it ends with "1", "01", "001" or "2", "02", "002" or alike, we convert it to PascalCase
    if (key.match(/^(.*?)(\d{1,3})$/)) {
        return changeCase.pascalCase(key)
    }

    switch (key) {
        case "resultColor":
            return "Result_Color"
        case "resultFloat":
            return "Result_Float"
        case "resultVector":
            return "Result_Vector"
        case "aColor":
            return "A_Color"
        case "aFloat":
            return "A_Float"
        case "aVector":
            return "A_Vector"
        case "bColor":
            return "B_Color"
        case "bFloat":
            return "B_Float"
        case "bVector":
            return "B_Vector"
        case "factorColor":
            return "Factor_Color"
        case "factorFloat":
            return "Factor_Float"
        case "factorVector":
            return "Factor_Vector"
        case "mapAssignmentTextureType":
            return "MapAssignment_TextureType"
    }

    const pascalCase = nodeClass === "TextureSet" || nodeClass === "ScannedTransmission" || nodeClass === "TextureFromSet"

    return pascalCase ? changeCase.pascalCase(key) : withAcronymsInCaps(changeCase.capitalCase(key))
}
